import React from "react";
import { useI18n } from "../i18n/I18nContext";
import NewsletterForm from "../components/NewsletterForm";
import heroImg from "../assets/hero.jpg";
import complexImg from "../assets/complex.jpg";
import constructionImg from "../assets/construction.jpg";
import renovationImg from "../assets/renovation.jpg";
import trustImg from "../assets/trust.jpg";
import residentialComplexImg from "../assets/residential_complex.jpg";

export default function Home() {
  const { t } = useI18n();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="bg-[#F8F9FA] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-6">
              {t("hero.overline")}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0F172A]">
              <span className="block">{t("hero.title_1")}</span>
              <span className="block italic text-[#1E3A8A]">
                {t("hero.title_2")}
              </span>
              <span className="block">{t("hero.title_3")}</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => scrollTo("newsletter")}
                className="px-8 py-4 bg-[#1E3A8A] text-white font-semibold text-sm uppercase tracking-[0.18em] hover:bg-[#152b6a]"
                data-testid="hero-cta-primary"
              >
                {t("hero.cta_primary")}
              </button>
              <button
                type="button"
                onClick={() => scrollTo("how-it-works")}
                className="px-8 py-4 border-2 border-[#0F172A] text-[#0F172A] font-semibold text-sm uppercase tracking-[0.18em] hover:bg-[#0F172A] hover:text-white"
                data-testid="hero-cta-secondary"
              >
                {t("hero.cta_secondary")}
              </button>
            </div>

            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-[#B48E5D]" /> {t("hero.badge_1")}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-[#B48E5D]" /> {t("hero.badge_2")}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-[#B48E5D]" /> {t("hero.badge_3")}
              </li>
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <img
                src={heroImg}
                alt=""
                loading="eager"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-slate-200 px-6 py-5 max-w-xs">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#B48E5D] font-bold mb-1">
                  {t("trust.stats.s1_label")}
                </p>
                <p className="font-heading text-3xl text-[#0F172A]">
                  {t("trust.stats.s1_value")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-4">
              {t("intro.overline")}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-[#0F172A]">
              {t("intro.title")}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-slate-700 leading-relaxed">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
            <p>{t("intro.p3")}</p>
            <p>{t("intro.p4")}</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-[#F8F9FA] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-4">
              {t("how_it_works.overline")}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight text-[#0F172A]">
              {t("how_it_works.title")}
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              {t("how_it_works.lead")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {["s1", "s2", "s3"].map((k, idx) => (
              <div
                key={k}
                className="bg-white border border-slate-200 p-8 flex flex-col"
                data-testid={`step-card-${idx + 1}`}
              >
                <span className="font-heading text-6xl text-[#B48E5D] leading-none">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-6 font-heading text-2xl text-[#0F172A]">
                  {t(`how_it_works.steps.${k}_title`)}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  {t(`how_it_works.steps.${k}_body`)}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-14 max-w-4xl text-slate-600 leading-relaxed border-l-2 border-[#B48E5D] pl-6">
            {t("how_it_works.footnote")}
          </p>
        </div>
      </section>

      {/* Opportunities */}
      <section id="opportunities" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-4">
                {t("opportunities.overline")}
              </p>
              <h2 className="font-heading text-3xl md:text-5xl leading-tight text-[#0F172A]">
                {t("opportunities.title")}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                {t("opportunities.lead")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <article className="md:col-span-4 md:row-span-2 bg-[#0F172A] text-white overflow-hidden flex flex-col">
              <div className="h-72 md:h-80 overflow-hidden">
                <img
                  src={complexImg}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:p-10 flex-1 flex flex-col justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-3">
                    01
                  </p>
                  <h3 className="font-heading text-2xl md:text-3xl">
                    {t("opportunities.cards.c1_title")}
                  </h3>
                  <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
                    {t("opportunities.cards.c1_body")}
                  </p>
                </div>
              </div>
            </article>

            <article className="md:col-span-2 bg-white border border-slate-200 overflow-hidden">
              <div className="h-40 overflow-hidden">
                <img
                  src={residentialComplexImg}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-2">
                  02
                </p>
                <h3 className="font-heading text-xl text-[#0F172A]">
                  {t("opportunities.cards.c2_title")}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {t("opportunities.cards.c2_body")}
                </p>
              </div>
            </article>

            <article className="md:col-span-2 bg-white border border-slate-200 overflow-hidden">
              <div className="h-40 overflow-hidden">
                <img
                  src={renovationImg}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-2">
                  03
                </p>
                <h3 className="font-heading text-xl text-[#0F172A]">
                  {t("opportunities.cards.c3_title")}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {t("opportunities.cards.c3_body")}
                </p>
              </div>
            </article>

            <article className="md:col-span-3 bg-white border border-slate-200 p-8">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-2">
                04
              </p>
              <h3 className="font-heading text-xl md:text-2xl text-[#0F172A]">
                {t("opportunities.cards.c4_title")}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                {t("opportunities.cards.c4_body")}
              </p>
            </article>

            <article className="md:col-span-3 bg-white border border-slate-200 overflow-hidden flex flex-col md:flex-row">
              <div className="md:w-1/2 h-40 md:h-auto overflow-hidden">
                <img
                  src={constructionImg}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 md:w-1/2">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-2">
                  05
                </p>
                <h3 className="font-heading text-xl md:text-2xl text-[#0F172A]">
                  {t("opportunities.cards.c5_title")}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {t("opportunities.cards.c5_body")}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Trust / Transparency */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src={trustImg}
              alt=""
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-4">
              {t("trust.overline")}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight">
              {t("trust.title")}
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              {t("trust.lead")}
            </p>
            <div className="mt-8 space-y-5 text-slate-300 leading-relaxed">
              <p>{t("trust.p1")}</p>
              <p>{t("trust.p2")}</p>
              <p>{t("trust.p3")}</p>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
              {["s1", "s2", "s3"].map((k) => (
                <div key={k}>
                  <p className="font-heading text-3xl md:text-4xl text-white">
                    {t(`trust.stats.${k}_value`)}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">
                    {t(`trust.stats.${k}_label`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#B48E5D] mb-4">
              {t("newsletter.overline")}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight text-[#0F172A]">
              {t("newsletter.title")}
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
              {t("newsletter.lead")}
            </p>
          </div>
          <div className="lg:col-span-6">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </main>
  );
}
